/**
 * MobileControls.ts — Virtual D-pad, throttle, and action buttons for touch devices.
 *
 * Provides:
 *   - A virtual D-pad (up/down/left/right → pitch & roll)
 *   - A throttle slider (vertical, replaces Shift/Ctrl)
 *   - Action buttons for Brake, Gear, Recover
 *   - Optional accelerometer (motion-control) mode as an alternative to D-pad
 *   - A "Menu" button to return to the start screen (replaces ESC key)
 *
 * All inputs are written to the same normalised axes that Controls.ts reads,
 * so the rest of the game doesn't need to know about touch input.
 */
const SETTINGS_KEY = 'skyrunner-mobile-settings';
export function loadMobileSettings() {
    try {
        const raw = localStorage.getItem(SETTINGS_KEY);
        if (raw)
            return { ...defaultSettings(), ...JSON.parse(raw) };
    }
    catch { /* ignore */ }
    return defaultSettings();
}
export function saveMobileSettings(s) {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
}
function defaultSettings() {
    return { dpadSide: 'right', inputMode: 'dpad', invertY: true, motionNeutralBeta: 30, motionNeutralGamma: 0, motionNeutralAngle: 0 };
}
/* ====================================================================== */
/*  Mobile detection                                                      */
/* ====================================================================== */
export function isTouchDevice() {
    return ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
}
/* ====================================================================== */
/*  Fullscreen helpers                                                    */
/* ====================================================================== */
export async function enterFullscreen() {
    const el = document.documentElement;
    try {
        if (el.requestFullscreen)
            await el.requestFullscreen();
        else if (el.webkitRequestFullscreen)
            await el.webkitRequestFullscreen();
    }
    catch { /* user declined or not supported */ }
}
export async function exitFullscreen() {
    try {
        if (document.fullscreenElement)
            await document.exitFullscreen();
        else if (document.webkitFullscreenElement)
            await document.webkitExitFullscreen();
    }
    catch { /* ignore */ }
}
export async function toggleFullscreen() {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
        await exitFullscreen();
    }
    else {
        await enterFullscreen();
        await tryLockLandscape();
    }
}
export async function tryLockLandscape() {
    try {
        const orientation = screen.orientation;
        if (orientation?.lock)
            await orientation.lock('landscape');
    }
    catch { /* not supported or denied */ }
}
/* ====================================================================== */
/*  Screen-orientation-aware axis helper                                  */
/* ====================================================================== */
/**
 * Returns the current screen orientation angle (0, 90, 180, 270).
 */
function getScreenAngle() {
    const o = screen.orientation;
    if (o && typeof o.angle === 'number')
        return o.angle;
    if (typeof window.orientation === 'number')
        return (window.orientation + 360) % 360;
    return 0;
}
/**
 * Transform raw deviceorientation beta/gamma into screen-relative
 * (pitch, roll) as the user perceives them in the current orientation.
 *
 *   angle   0 → portrait            : pitch = beta,   roll = gamma
 *   angle  90 → landscape (CCW)     : pitch = gamma,  roll = -beta
 *   angle 270 → landscape (CW)      : pitch = -gamma, roll = beta
 *   angle 180 → portrait upside-down: pitch = -beta,  roll = -gamma
 */
function screenRelativeOrientation(beta, gamma, angle) {
    switch (angle) {
        case 90: return { pitch: gamma, roll: -beta };
        case 270: return { pitch: -gamma, roll: beta };
        case 180: return { pitch: -beta, roll: -gamma };
        default: return { pitch: beta, roll: gamma };
    }
}
/* ====================================================================== */
/*  MobileControls class                                                  */
/* ====================================================================== */
export class MobileControls {
    constructor(game, controls) {
        /* D-pad state */
        this.dpadTouchId = null;
        this.dpadCenterX = 0;
        this.dpadCenterY = 0;
        this.dpadInputX = 0; // roll  (-1…+1)
        this.dpadInputY = 0; // pitch (-1…+1)
        /* Throttle state */
        this.throttleTouchId = null;
        this.throttleValue = 0.5; // 0…1, written directly to player.throttle
        /* Motion control */
        this.motionEnabled = false;
        this.motionBeta = 0; // pitch (forward/back tilt)
        this.motionGamma = 0; // roll  (left/right tilt)
        this.onDeviceOrientation = null;
        this.game = game;
        this.controls = controls;
        this.settings = loadMobileSettings();
        this.container = document.getElementById('mobile-controls');
        this.buildUI();
        this.attachEvents();
        if (this.settings.inputMode === 'motion') {
            this.enableMotionControl();
        }
    }
    /* ------------------------------------------------------------------ */
    /*  Build DOM                                                          */
    /* ------------------------------------------------------------------ */
    buildUI() {
        this.container.innerHTML = '';
        this.container.classList.remove('hidden');
        /* D-pad */
        this.dpadEl = document.createElement('div');
        this.dpadEl.className = 'mobile-dpad';
        this.dpadEl.innerHTML = `
      <div class="dpad-ring">
        <div class="dpad-knob"></div>
      </div>
    `;
        /* Throttle slider */
        this.throttleEl = document.createElement('div');
        this.throttleEl.className = 'mobile-throttle';
        this.throttleEl.innerHTML = `
      <div class="throttle-track">
        <div class="throttle-fill"></div>
        <div class="throttle-handle"></div>
      </div>
      <span class="throttle-label">THR</span>
    `;
        /* Action buttons */
        this.actionsEl = document.createElement('div');
        this.actionsEl.className = 'mobile-actions';
        this.actionsEl.innerHTML = `
      <button class="mobile-btn" data-action="brake">BRK</button>
      <button class="mobile-btn" data-action="gear">GEAR</button>
      <button class="mobile-btn" data-action="recover">REC</button>
    `;
        /* Menu button */
        this.menuBtn = document.createElement('button');
        this.menuBtn.className = 'mobile-menu-btn';
        this.menuBtn.textContent = '☰';
        /* Calibrate button (shown only in motion mode) */
        this.calibrateBtn = document.createElement('button');
        this.calibrateBtn.className = 'mobile-calibrate-btn';
        this.calibrateBtn.textContent = '⊕ Calibrate';
        if (this.settings.inputMode !== 'motion')
            this.calibrateBtn.classList.add('hidden');
        this.layoutForSide();
        this.container.append(this.dpadEl, this.throttleEl, this.actionsEl, this.menuBtn, this.calibrateBtn);
        /* Hide D-pad ring if using motion */
        if (this.settings.inputMode === 'motion') {
            this.dpadEl.classList.add('hidden');
        }
    }
    layoutForSide() {
        const dpadLeft = this.settings.dpadSide === 'left';
        // D-pad goes on its configured side
        this.dpadEl.classList.toggle('mobile-left', dpadLeft);
        this.dpadEl.classList.toggle('mobile-right', !dpadLeft);
        // Throttle on opposite side (outer edge)
        this.throttleEl.classList.toggle('mobile-left', !dpadLeft);
        this.throttleEl.classList.toggle('mobile-right', dpadLeft);
        // Action buttons next to the D-pad (inner side)
        this.actionsEl.classList.toggle('actions-left', dpadLeft);
        this.actionsEl.classList.toggle('actions-right', !dpadLeft);
    }
    /* ------------------------------------------------------------------ */
    /*  Touch events                                                       */
    /* ------------------------------------------------------------------ */
    attachEvents() {
        /* D-pad touch */
        this.dpadEl.addEventListener('touchstart', (e) => this.onDpadStart(e), { passive: false });
        this.dpadEl.addEventListener('touchmove', (e) => this.onDpadMove(e), { passive: false });
        this.dpadEl.addEventListener('touchend', (e) => this.onDpadEnd(e), { passive: false });
        this.dpadEl.addEventListener('touchcancel', (e) => this.onDpadEnd(e), { passive: false });
        /* Throttle touch */
        this.throttleEl.addEventListener('touchstart', (e) => this.onThrottleStart(e), { passive: false });
        this.throttleEl.addEventListener('touchmove', (e) => this.onThrottleMove(e), { passive: false });
        this.throttleEl.addEventListener('touchend', () => this.onThrottleEnd(), { passive: false });
        this.throttleEl.addEventListener('touchcancel', () => this.onThrottleEnd(), { passive: false });
        /* Action buttons */
        this.actionsEl.addEventListener('touchstart', (e) => this.onActionStart(e), { passive: false });
        this.actionsEl.addEventListener('touchend', (e) => this.onActionEnd(e), { passive: false });
        this.actionsEl.addEventListener('touchcancel', (e) => this.onActionEnd(e), { passive: false });
        /* Menu button */
        this.menuBtn.addEventListener('click', () => this.game.returnToMenu());
        /* Calibrate button */
        this.calibrateBtn.addEventListener('click', () => this.calibrateMotion());
    }
    /* --- D-pad handlers --- */
    onDpadStart(e) {
        e.preventDefault();
        if (this.dpadTouchId !== null)
            return;
        const t = e.changedTouches[0];
        this.dpadTouchId = t.identifier;
        const rect = this.dpadEl.querySelector('.dpad-ring').getBoundingClientRect();
        this.dpadCenterX = rect.left + rect.width / 2;
        this.dpadCenterY = rect.top + rect.height / 2;
        this.updateDpadFromTouch(t);
    }
    onDpadMove(e) {
        e.preventDefault();
        for (let i = 0; i < e.changedTouches.length; i++) {
            const t = e.changedTouches[i];
            if (t.identifier === this.dpadTouchId) {
                this.updateDpadFromTouch(t);
                break;
            }
        }
    }
    onDpadEnd(e) {
        for (let i = 0; i < e.changedTouches.length; i++) {
            if (e.changedTouches[i].identifier === this.dpadTouchId) {
                this.dpadTouchId = null;
                this.dpadInputX = 0;
                this.dpadInputY = 0;
                this.updateKnobPosition(0, 0);
                break;
            }
        }
    }
    updateDpadFromTouch(t) {
        const dx = t.clientX - this.dpadCenterX;
        const dy = t.clientY - this.dpadCenterY;
        const maxR = 45;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const clamped = Math.min(dist, maxR);
        const angle = Math.atan2(dy, dx);
        const nx = (Math.cos(angle) * clamped) / maxR;
        const ny = (Math.sin(angle) * clamped) / maxR;
        this.dpadInputX = nx;
        this.dpadInputY = ny;
        this.updateKnobPosition(nx, ny);
    }
    updateKnobPosition(nx, ny) {
        const knob = this.dpadEl.querySelector('.dpad-knob');
        if (!knob)
            return;
        const px = nx * 35;
        const py = ny * 35;
        knob.style.transform = `translate(${px}px, ${py}px)`;
    }
    /* --- Throttle handlers --- */
    onThrottleStart(e) {
        e.preventDefault();
        if (this.throttleTouchId !== null)
            return;
        const t = e.changedTouches[0];
        this.throttleTouchId = t.identifier;
        this.updateThrottleFromTouch(t);
    }
    onThrottleMove(e) {
        e.preventDefault();
        for (let i = 0; i < e.changedTouches.length; i++) {
            const t = e.changedTouches[i];
            if (t.identifier === this.throttleTouchId) {
                this.updateThrottleFromTouch(t);
                break;
            }
        }
    }
    onThrottleEnd() {
        this.throttleTouchId = null;
    }
    updateThrottleFromTouch(t) {
        const track = this.throttleEl.querySelector('.throttle-track');
        if (!track)
            return;
        const rect = track.getBoundingClientRect();
        const relY = (t.clientY - rect.top) / rect.height;
        this.throttleValue = 1 - Math.max(0, Math.min(1, relY));
        this.updateThrottleVisual();
    }
    updateThrottleVisual() {
        const fill = this.throttleEl.querySelector('.throttle-fill');
        const handle = this.throttleEl.querySelector('.throttle-handle');
        if (!fill || !handle)
            return;
        fill.style.height = `${this.throttleValue * 100}%`;
        handle.style.bottom = `${this.throttleValue * 100}%`;
    }
    /* --- Action button handlers --- */
    onActionStart(e) {
        e.preventDefault();
        const btn = e.target.closest('.mobile-btn');
        if (!btn)
            return;
        btn.classList.add('active');
        const action = btn.dataset.action;
        if (action === 'brake')
            this.controls.brake = true;
        if (action === 'gear')
            this.game.player.gearDown = !this.game.player.gearDown;
        if (action === 'recover')
            this.controls.recover = true;
    }
    onActionEnd(e) {
        const btn = e.target.closest('.mobile-btn');
        if (!btn)
            return;
        btn.classList.remove('active');
        const action = btn.dataset.action;
        if (action === 'brake')
            this.controls.brake = false;
    }
    /* ------------------------------------------------------------------ */
    /*  Motion control (accelerometer)                                     */
    /* ------------------------------------------------------------------ */
    enableMotionControl() {
        this.motionEnabled = true;
        this.dpadEl.classList.add('hidden');
        this.calibrateBtn?.classList.remove('hidden');
        this.onDeviceOrientation = (e) => {
            this.motionBeta = (e.beta ?? 0);
            this.motionGamma = (e.gamma ?? 0);
        };
        window.addEventListener('deviceorientation', this.onDeviceOrientation);
    }
    disableMotionControl() {
        this.motionEnabled = false;
        this.dpadEl.classList.remove('hidden');
        this.calibrateBtn?.classList.add('hidden');
        if (this.onDeviceOrientation) {
            window.removeEventListener('deviceorientation', this.onDeviceOrientation);
            this.onDeviceOrientation = null;
        }
    }
    /** Snapshot the current device orientation as the new "neutral" hold position. */
    calibrateMotion() {
        this.settings.motionNeutralBeta = this.motionBeta;
        this.settings.motionNeutralGamma = this.motionGamma;
        this.settings.motionNeutralAngle = getScreenAngle();
        saveMobileSettings(this.settings);
        // Brief visual feedback
        this.calibrateBtn.textContent = '✓ Set!';
        setTimeout(() => { this.calibrateBtn.textContent = '⊕ Calibrate'; }, 800);
    }
    /* ------------------------------------------------------------------ */
    /*  Per-frame update: write values into Controls                       */
    /* ------------------------------------------------------------------ */
    update(_dt) {
        const invertY = this.settings.invertY ? -1 : 1;
        if (this.motionEnabled) {
            // Transform raw device values through current screen orientation
            const angle = getScreenAngle();
            const current = screenRelativeOrientation(this.motionBeta, this.motionGamma, angle);
            const neutral = screenRelativeOrientation(this.settings.motionNeutralBeta, this.settings.motionNeutralGamma, this.settings.motionNeutralAngle);
            const pitchRange = 40;
            const rollRange = 35;
            const pitchRaw = -(current.pitch - neutral.pitch) / pitchRange;
            const rollRaw = (current.roll - neutral.roll) / rollRange;
            this.controls.pitchInput = Math.max(-1, Math.min(1, pitchRaw * invertY));
            this.controls.rollInput = Math.max(-1, Math.min(1, -rollRaw));
        }
        else {
            // D-pad
            this.controls.rollInput = -this.dpadInputX;
            this.controls.pitchInput = -this.dpadInputY * invertY;
        }
        // Throttle — write directly to player
        this.game.player.throttle = this.throttleValue;
        this.controls.throttleInput = 0; // don't let keyboard throttle fight
        // Update throttle visual every frame (in case something else changed throttle)
        this.updateThrottleVisual();
    }
    /* ------------------------------------------------------------------ */
    /*  Settings                                                           */
    /* ------------------------------------------------------------------ */
    applySettings(s) {
        this.settings = s;
        saveMobileSettings(s);
        this.layoutForSide();
        if (s.inputMode === 'motion')
            this.enableMotionControl();
        else
            this.disableMotionControl();
    }
    getSettings() { return { ...this.settings }; }
    /* ------------------------------------------------------------------ */
    /*  Cleanup                                                            */
    /* ------------------------------------------------------------------ */
    dispose() {
        this.disableMotionControl();
        this.container.innerHTML = '';
        this.container.classList.add('hidden');
    }
}
